import { getStructure, type Zone } from "@two-crowns/data";
import { buildZoneLayout } from "./layout";
import type { Vec2 } from "./movement";
import { BRIDGE_HALF_L, BRIDGE_HALF_W } from "./terrain";

/** `h` = height in meters, used for camera occlusion. */
export type Collider =
  | { kind: "circle"; x: number; z: number; r: number; h: number }
  | {
      kind: "box";
      x: number;
      z: number;
      hw: number;
      hd: number;
      cos: number;
      sin: number;
      h: number;
    };

/**
 * Step of sweep(): well under the thinnest thing that blocks (a wall post is 1.1 m across),
 * so a straight move cannot step over it.
 */
const SWEEP_STEP_M = 0.2;
/** A resolve that moves the body less than this is touching, not blocked. */
const SWEEP_TOUCH_M = 0.02;

/** Largest body radius we resolve against (mobs up to 1 m, players 0.4 m). */
const MAX_BODY_RADIUS = 1.2;
const PASSES = 3;
/** Town wall blocks (one per post): 1.2 m thick, 4.3 m high. */
const POST_RADIUS = 0.55;
const POST_HEIGHT = 4.3;
/** Only trunks block, so walking under canopies works. */
const TREE_TRUNK_RADIUS = 0.45;

function extent(c: Collider): number {
  return c.kind === "circle" ? c.r : Math.hypot(c.hw, c.hd);
}

/**
 * Static collision for a zone: uniform grid of circles and oriented boxes.
 * Shared by server movement and client prediction so both resolve identically.
 */
export class CollisionGrid {
  private readonly cells = new Map<number, Collider[]>();

  constructor(
    readonly colliders: Collider[],
    private readonly cellSize = 8,
  ) {
    for (const c of colliders) {
      const e = extent(c) + MAX_BODY_RADIUS;
      const x0 = Math.floor((c.x - e) / cellSize);
      const x1 = Math.floor((c.x + e) / cellSize);
      const z0 = Math.floor((c.z - e) / cellSize);
      const z1 = Math.floor((c.z + e) / cellSize);
      for (let x = x0; x <= x1; x++) {
        for (let z = z0; z <= z1; z++) {
          const key = this.key(x, z);
          let list = this.cells.get(key);
          if (!list) {
            list = [];
            this.cells.set(key, list);
          }
          list.push(c);
        }
      }
    }
  }

  private key(cx: number, cz: number): number {
    return (cx + 4096) * 8192 + (cz + 4096);
  }

  /** Height of the tallest collider covering (x, z), or 0. */
  heightAt(x: number, z: number): number {
    const list = this.cells.get(
      this.key(Math.floor(x / this.cellSize), Math.floor(z / this.cellSize)),
    );
    if (!list) return 0;
    let h = 0;
    for (const c of list) {
      if (c.h <= h) continue;
      const dx = x - c.x;
      const dz = z - c.z;
      if (c.kind === "circle") {
        if (dx * dx + dz * dz <= c.r * c.r) h = c.h;
      } else {
        const lx = dx * c.cos - dz * c.sin;
        const lz = dx * c.sin + dz * c.cos;
        if (Math.abs(lx) <= c.hw && Math.abs(lz) <= c.hd) h = c.h;
      }
    }
    return h;
  }

  /** Pushes a body of `radius` out of every collider it overlaps. */
  resolve(p: Vec2, radius: number): Vec2 {
    const list = this.cells.get(
      this.key(Math.floor(p.x / this.cellSize), Math.floor(p.z / this.cellSize)),
    );
    if (!list) return p;
    let x = p.x;
    let z = p.z;
    for (let pass = 0; pass < PASSES; pass++) {
      let moved = false;
      for (const c of list) {
        if (c.kind === "circle") {
          const dx = x - c.x;
          const dz = z - c.z;
          const min = c.r + radius;
          const d2 = dx * dx + dz * dz;
          if (d2 >= min * min) continue;
          const d = Math.sqrt(d2);
          if (d === 0) {
            x = c.x + min;
          } else {
            x = c.x + (dx / d) * min;
            z = c.z + (dz / d) * min;
          }
          moved = true;
        } else {
          const dx = x - c.x;
          const dz = z - c.z;
          let lx = dx * c.cos - dz * c.sin;
          let lz = dx * c.sin + dz * c.cos;
          const qx = Math.max(-c.hw, Math.min(c.hw, lx));
          const qz = Math.max(-c.hd, Math.min(c.hd, lz));
          if (qx === lx && qz === lz) {
            // Center inside the box: leave through the nearest face.
            const px = c.hw - Math.abs(lx);
            const pz = c.hd - Math.abs(lz);
            if (px < pz) lx = Math.sign(lx || 1) * (c.hw + radius);
            else lz = Math.sign(lz || 1) * (c.hd + radius);
          } else {
            const ex = lx - qx;
            const ez = lz - qz;
            const d2 = ex * ex + ez * ez;
            if (d2 >= radius * radius) continue;
            const d = Math.sqrt(d2);
            lx = qx + (ex / d) * radius;
            lz = qz + (ez / d) * radius;
          }
          x = c.x + lx * c.cos + lz * c.sin;
          z = c.z - lx * c.sin + lz * c.cos;
          moved = true;
        }
      }
      if (!moved) break;
    }
    return x === p.x && z === p.z ? p : { x, z };
  }

  /**
   * Moves a body in a straight line from `from` toward `to` and stops at the first collider
   * in the way. For instant moves (blinks, charges): resolving only the landing spot lets a
   * body hop a thin wall or fence when there is room on the other side.
   */
  sweep(from: Vec2, to: Vec2, radius: number): Vec2 {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / SWEEP_STEP_M));
    let last = this.resolve(from, radius);
    for (let i = 1; i <= steps; i++) {
      const p = { x: from.x + (dx * i) / steps, z: from.z + (dz * i) / steps };
      const r = this.resolve(p, radius);
      if (Math.hypot(r.x - p.x, r.z - p.z) > SWEEP_TOUCH_M) return last;
      last = r;
    }
    return last;
  }
}

export function zoneColliders(zone: Zone): Collider[] {
  const layout = buildZoneLayout(zone);
  const out: Collider[] = [];
  for (const s of [...layout.structures, ...layout.nature]) {
    const def = getStructure(s.type);
    if (def.collider === "none") continue;
    if (def.collider === "circle") {
      const r = s.type.startsWith("tree_") ? TREE_TRUNK_RADIUS : Math.max(def.w, def.d) / 2;
      out.push({ kind: "circle", x: s.x, z: s.z, r: r * s.scale, h: def.h * s.scale });
    } else {
      out.push({
        kind: "box",
        x: s.x,
        z: s.z,
        hw: (def.w / 2) * s.scale,
        hd: (def.d / 2) * s.scale,
        cos: Math.cos(s.rot),
        sin: Math.sin(s.rot),
        h: def.h * s.scale,
      });
    }
  }
  for (const p of layout.posts)
    out.push({ kind: "circle", x: p.x, z: p.z, r: POST_RADIUS, h: POST_HEIGHT });
  out.push(...deepWater(zone));
  return out;
}

/** How far into a deep river's edge you can wade before the current stops you. */
const WADE_M = 1.5;

/**
 * A deep river blocks like a wall, except where a bridge crosses it: circles along its middle,
 * left out under each bridge deck. Height 0, so the camera looks straight through it.
 */
function deepWater(zone: Zone): Collider[] {
  const out: Collider[] = [];
  const bridges = (zone.town?.structures ?? [])
    .filter((s) => s.type === "bridge")
    .map((s) => ({
      x: s.x,
      z: s.z,
      cos: Math.cos((s.rot * Math.PI) / 180),
      sin: Math.sin((s.rot * Math.PI) / 180),
    }));
  for (const river of zone.world?.water.rivers ?? []) {
    if (!river.deep) continue;
    const r = river.width / 2 - WADE_M;
    const underBridge = (x: number, z: number) =>
      bridges.some((b) => {
        const dx = x - b.x;
        const dz = z - b.z;
        const across = dx * b.cos - dz * b.sin;
        const along = dx * b.sin + dz * b.cos;
        return Math.abs(across) < BRIDGE_HALF_W + r && Math.abs(along) < BRIDGE_HALF_L;
      });
    for (let i = 1; i < river.points.length; i++) {
      const [ax, az] = river.points[i - 1] as [number, number];
      const [bx, bz] = river.points[i] as [number, number];
      const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az)));
      for (let k = 0; k < steps; k++) {
        const x = ax + ((bx - ax) * k) / steps;
        const z = az + ((bz - az) * k) / steps;
        if (!underBridge(x, z)) out.push({ kind: "circle", x, z, r, h: 0 });
      }
    }
  }
  return out;
}

const gridCache = new Map<string, CollisionGrid>();

export function zoneCollisionGrid(zone: Zone): CollisionGrid {
  let grid = gridCache.get(zone.id);
  if (!grid) {
    grid = new CollisionGrid(zoneColliders(zone));
    gridCache.set(zone.id, grid);
  }
  return grid;
}
